<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex items-center justify-between space-x-2">
            <h1 class="text-2xl font-semibold text-gray-700">Distributors</h1>
            <div class="flex items-center space-x-2">
                <UInput
                    v-model="search"
                    type="text"
                    placeholder="Search..."
                    icon="i-ph-magnifying-glass-duotone"
                />
                <UButton @click="overlays.create = true" color="black" icon="i-ph-plus-bold">
                    Add Distributor
                </UButton>
            </div>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="distributors"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-storefront-duotone', label: 'No distributors.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3' } }"
                class="h-full"
            >
                <template #avatar-data="{ row }">
                    <div class="flex flex-col items-center">
                        <Avatar bucket="media" folder="partners" size="md" :placeholder="row.name" :path="row.avatar" />
                    </div>
                </template>
                <template #actions-data="{ row }">
                    <UDropdown :items="actions(row)">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </template>
            </UTable>
        </div>

        <OverlayPartnerCreateDistributor v-model:open="overlays.create" @reload="load" />
        <OverlayPartnerCreatePartnerUser
            v-model:open="overlays.createUser"
            :partners="scopedPartners"
            :partner-id="selectedId"
        />
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import type { ScopedPartner } from '~/composables/usePartnerOverview'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.partner] })
useHead({ title: 'Distributors' })

const { overview, scopedPartners, load: loadOverview } = usePartnerOverview()

const loading = ref<boolean>(false)
const search = ref<string>('')
const selectedId = ref<string>()

const overlays = ref({
    create: false,
    createUser: false,
})

const columns = [
    { key: 'avatar', label: '', class: 'w-16' },
    { key: 'name', label: 'Name' },
    { key: 'actions', label: '', class: 'w-16' },
]

const distributors = computed(() => {
    const q = search.value.trim().toLowerCase()
    return (overview.value?.distributors ?? []).filter(d => !q || d.name.toLowerCase().includes(q))
})

onMounted(() => load())

async function load() {
    loading.value = true
    const data = await loadOverview()
    loading.value = false

    if (!data.canManageDistributors) {
        navigateTo('/activations', { replace: true })
    }
}

function actions(row: ScopedPartner) {
    return [[{
        label: 'View Activations',
        icon: 'i-ph-shield-check-duotone',
        click: () => navigateTo({ path: '/activations', query: { partner_id: row.id } }),
    }, {
        label: 'Add User',
        icon: 'i-ph-user-plus-duotone',
        click: () => {
            selectedId.value = row.id
            overlays.value.createUser = true
        },
    }]]
}
</script>
